import React, { useState, useEffect } from 'react';
import './Navbar.css';

const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [theme, setTheme] = useState(() => {
        return localStorage.getItem('theme') || 'light';
    });
    const [activeSection, setActiveSection] = useState('home');

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);

    useEffect(() => {
        const handleScroll = () => {
            const sections = ['home', 'categories', 'contact'];
            const scrollPosition = window.scrollY + 100;

            for (const section of sections) {
                const element = document.getElementById(section);
                if (element) {
                    const offsetTop = element.offsetTop;
                    const offsetBottom = offsetTop + element.offsetHeight;

                    if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
                        setActiveSection(section);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        setIsSearchOpen(false); // Close search when menu opens
        document.body.style.overflow = !isMenuOpen ? 'hidden' : '';
    };

    const toggleSearch = () => {
        setIsSearchOpen(!isSearchOpen);
    };

    const toggleTheme = () => {
        setTheme(theme === 'light' ? 'dark' : 'light');
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
        document.body.style.overflow = '';
    };

    const handleNavClick = (e, sectionId) => {
        e.preventDefault();
        closeMenu();
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            // Search logic here
            performSearch(searchQuery);
        }
    };

    const performSearch = (query) => {
        // Normalize search query
        const normalizedQuery = query.toLowerCase().trim();
        
        // Define searchable categories and their keywords
        const categoryKeywords = {
            'professional-services': ['professional', 'service', 'consulting', 'logistics', 'procurement', 'supply chain', 'business'],
            'visa-pro-services': ['visa', 'pro', 'pro services', 'employment visa', 'trade license', 'attestation', 'government', 'immigration', 'sponsorship'],
            'ecommerce': ['ecommerce', 'e-commerce', 'online', 'shop', 'digital', 'retail', 'delivery', 'fulfilment'],
            'hospitality-essentials': ['hospitality', 'hotel', 'towel', 'linen', 'bathrobe', 'amenity', 'amenities', 'guest'],
            'food-provisions': ['food', 'provision', 'pulse', 'grain', 'lentil', 'rice', 'wheat', 'chickpea', 'bean', 'quinoa'],
            'cleaning-hygiene': ['clean', 'cleaning', 'hygiene', 'sanitation', 'disinfectant', 'sanitiser', 'hygenic', 'wash'],
            'tableware-kitchenware': ['tableware', 'kitchenware', 'plate', 'cutlery', 'glass', 'utensil', 'cookware', 'dining', 'kitchen'],
            'furniture': ['furniture', 'sofa', 'chair', 'table', 'bed', 'desk', 'cabinet', 'storage', 'furnishing']
        };

        // Find matching category
        let matchedCategory = null;
        for (const [categoryId, keywords] of Object.entries(categoryKeywords)) {
            if (keywords.some(keyword => normalizedQuery.includes(keyword))) {
                matchedCategory = categoryId;
                break;
            }
        }

        if (matchedCategory) {
            // Scroll to categories section
            const categoriesSection = document.getElementById('categories');
            if (categoriesSection) {
                categoriesSection.scrollIntoView({ behavior: 'smooth' });
                
                // After scrolling, trigger the category card click
                setTimeout(() => {
                    const categoryCard = document.querySelector(`[data-category="${matchedCategory}"]`);
                    if (categoryCard) {
                        categoryCard.click();
                    }
                }, 500);
            }
        } else {
            // If no match, just scroll to categories
            const categoriesSection = document.getElementById('categories');
            if (categoriesSection) {
                categoriesSection.scrollIntoView({ behavior: 'smooth' });
            }
            alert(`No exact match found for "${query}". Showing all categories.`);
        }

        // Clear search
        setSearchQuery('');
        setIsSearchOpen(false);
    };

    // Logo path based on theme
    const logoSrc = theme === 'dark' 
        ? '/assets/aroha-dark.jpeg' 
        : '/assets/aroha-light.jpeg';

    const whatsappNumber = '971589532066';
    const whatsappLink = `https://wa.me/${whatsappNumber}`;

    return (
        <>
            <nav className="nav">
                <div className="nav-container">
                    <a href="#home" onClick={(e) => handleNavClick(e, 'home')} aria-label="Aroha Home">
                        <img src={logoSrc} alt="Aroha" className="nav-logo" />
                    </a>

                    <ul className="nav-menu">
                        <li>
                            <a 
                                href="#home" 
                                className={`nav-link ${activeSection === 'home' ? 'active' : ''}`}
                                onClick={(e) => handleNavClick(e, 'home')}
                            >
                                Home
                            </a>
                        </li>
                        <li>
                            <a 
                                href="#categories" 
                                className={`nav-link ${activeSection === 'categories' ? 'active' : ''}`}
                                onClick={(e) => handleNavClick(e, 'categories')}
                            >
                                Categories
                            </a>
                        </li>
                        <li>
                            <a 
                                href="#contact" 
                                className={`nav-link ${activeSection === 'contact' ? 'active' : ''}`}
                                onClick={(e) => handleNavClick(e, 'contact')}
                            >
                                Contact Us
                            </a>
                        </li>
                    </ul>

                    {/* Desktop Search */}
                    <div className="nav-search">
                        <form onSubmit={handleSearch}>
                            <svg className="nav-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"/>
                                <path d="m21 21-4.35-4.35"/>
                            </svg>
                            <input 
                                type="search" 
                                className="nav-search-input" 
                                placeholder="Search products..." 
                                aria-label="Search"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </form>
                    </div>

                    <div className="nav-contact">
                        <a href="tel:+971589532066" className="nav-contact-item-icon" aria-label="Call us">
                            <svg className="nav-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                            </svg>
                        </a>
                        <a href="mailto:hello@arohaint.com" className="nav-contact-item-icon" aria-label="Email us">
                            <svg className="nav-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                <polyline points="22,6 12,13 2,6"/>
                            </svg>
                        </a>
                        <a href={whatsappLink} className="nav-contact-item-icon" aria-label="WhatsApp us">
                            <svg className="nav-contact-icon" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm0 18.13a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.11.82.83-3.03-.2-.31a8.19 8.19 0 0 1-1.26-4.37c0-4.54 3.7-8.23 8.24-8.23 2.2 0 4.27.86 5.82 2.42a8.17 8.17 0 0 1 2.41 5.82c0 4.54-3.7 8.23-8.24 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.12-1.04-.38-1.99-1.22-.73-.66-1.23-1.47-1.37-1.72-.14-.24-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.15.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.42-.14-.01-.31-.01-.48-.01-.16 0-.43.06-.66.31-.23.24-.86.85-.86 2.06 0 1.22.89 2.4 1.01 2.56.12.16 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.1-.23-.16-.48-.28z"/>
                            </svg>
                        </a>
                    </div>

                    <button className="theme-toggle-desktop" onClick={toggleTheme} aria-label="Toggle theme">
                        {theme === 'light' ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                            </svg>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="5"/>
                                <line x1="12" y1="1" x2="12" y2="3"/>
                                <line x1="12" y1="21" x2="12" y2="23"/>
                                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                                <line x1="1" y1="12" x2="3" y2="12"/>
                                <line x1="21" y1="12" x2="23" y2="12"/>
                                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                            </svg>
                        )}
                    </button>

                    {/* Mobile/Tablet Right Section */}
                    <div className="nav-right">
                        {/* Mobile Search Icon */}
                        <button className="search-icon-btn" onClick={toggleSearch} aria-label="Search">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"/>
                                <path d="m21 21-4.35-4.35"/>
                            </svg>
                        </button>

                        <button className={`hamburger ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu} aria-label="Menu">
                            <span className="hamburger-line"></span>
                            <span className="hamburger-line"></span>
                            <span className="hamburger-line"></span>
                        </button>
                    </div>
                </div>

                {/* Mobile Search Bar */}
                {isSearchOpen && (
                    <div className="mobile-search-bar">
                        <form onSubmit={handleSearch}>
                            <input 
                                type="search" 
                                className="mobile-search-input" 
                                placeholder="Search products..." 
                                aria-label="Search"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                autoFocus
                            />
                            <button type="submit" className="mobile-search-submit">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="11" cy="11" r="8"/>
                                    <path d="m21 21-4.35-4.35"/>
                                </svg>
                            </button>
                        </form>
                    </div>
                )}
            </nav>

            {/* Mobile Menu */}
            <div className={`mobile-menu ${isMenuOpen ? 'active' : ''}`}>
                <ul className="mobile-nav-links">
                    <li>
                        <a href="#home" className="mobile-nav-link" onClick={(e) => handleNavClick(e, 'home')}>
                            Home
                        </a>
                    </li>
                    <li>
                        <a href="#categories" className="mobile-nav-link" onClick={(e) => handleNavClick(e, 'categories')}>
                            Categories
                        </a>
                    </li>
                    <li>
                        <a href="#contact" className="mobile-nav-link" onClick={(e) => handleNavClick(e, 'contact')}>
                            Contact Us
                        </a>
                    </li>
                </ul>

                <div className="mobile-contact">
                    <a href="tel:+971589532066" className="nav-contact-item">
                        <svg className="nav-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                        </svg>
                        <span>+971 58 953 2066</span>
                    </a>
                    <a href="mailto:hello@arohaint.com" className="nav-contact-item">
                        <svg className="nav-contact-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                            <polyline points="22,6 12,13 2,6"/>
                        </svg>
                        <span>hello@arohaint.com</span>
                    </a>
                    <a href={whatsappLink} className="nav-contact-item">
                        <svg className="nav-contact-icon" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                            <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm0 18.13a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.11.82.83-3.03-.2-.31a8.19 8.19 0 0 1-1.26-4.37c0-4.54 3.7-8.23 8.24-8.23 2.2 0 4.27.86 5.82 2.42a8.17 8.17 0 0 1 2.41 5.82c0 4.54-3.7 8.23-8.24 8.23zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.78.97-.14.16-.29.18-.54.06-.25-.12-1.04-.38-1.99-1.22-.73-.66-1.23-1.47-1.37-1.72-.14-.24-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.15.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.42-.14-.01-.31-.01-.48-.01-.16 0-.43.06-.66.31-.23.24-.86.85-.86 2.06 0 1.22.89 2.4 1.01 2.56.12.16 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.1-.23-.16-.48-.28z"/>
                        </svg>
                        <span>+971 58 953 2066</span>
                    </a>
                </div>

                <div className="mobile-theme-toggle">
                    <button className="theme-toggle-btn" onClick={toggleTheme}>
                        {theme === 'light' ? (
                            <>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                                </svg>
                                <span>Dark Mode</span>
                            </>
                        ) : (
                            <>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="5"/>
                                    <line x1="12" y1="1" x2="12" y2="3"/>
                                    <line x1="12" y1="21" x2="12" y2="23"/>
                                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                                    <line x1="1" y1="12" x2="3" y2="12"/>
                                    <line x1="21" y1="12" x2="23" y2="12"/>
                                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                                </svg>
                                <span>Light Mode</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </>
    );
};

export default Navbar;